import type { DealPartnerDraft, ProductDealType } from "@/lib/catalog/dealPartners";
import type { ID, ProductDraft, PurchaseCostLine } from "@/lib/catalog/types";
import type { PartyOption } from "@/lib/parties/partiesStore";

export type PaidByOption = {
  value: string;
  label: string;
  kind: "party";
  id: string;
};

export function parsePaidBySelectValue(value: string | null | undefined): {
  paidByPartyId: string | null;
} {
  const raw = value == null ? "" : String(value).trim();
  if (!raw || raw === "__none__") {
    return { paidByPartyId: null };
  }
  return { paidByPartyId: raw };
}

export function paidBySelectValue(line: Pick<PurchaseCostLine, "paidByPartyId">): string {
  if (line.paidByPartyId != null && String(line.paidByPartyId).trim() !== "") {
    return String(line.paidByPartyId);
  }
  return "__none__";
}

/**
 * Paid By options for additional cost lines:
 * - Partnership: Selected deal partners + Showroom (company)
 * - Seller Vehicle (owner_vehicle): Vehicle Owner + Showroom (company)
 * - Purchased: Showroom (company)
 */
export function buildPaidByOptions(input: {
  dealType?: ProductDealType | null | undefined;
  companyParties?: PartyOption[] | undefined;
  dealPartners?: DealPartnerDraft[] | undefined;
  parties?: PartyOption[] | undefined;
}): PaidByOption[] {
  const options: PaidByOption[] = [];
  const seenPartyIds = new Set<string>();

  // Always include all is_company = 1 parties (Showroom / Company parties) first
  const companyPartiesList = [
    ...(input.companyParties ?? []),
    ...(input.parties ?? []).filter((p) => Boolean(p.isCompany)),
  ];

  for (const party of companyPartiesList) {
    const partyId = String(party.id);
    if (!partyId || seenPartyIds.has(partyId)) continue;
    seenPartyIds.add(partyId);
    options.push({
      value: partyId,
      label: party.name?.trim() || `Showroom #${partyId}`,
      kind: "party",
      id: partyId,
    });
  }

  const partyById = new Map((input.parties ?? []).map((p) => [String(p.id), p]));
  const dealType = input.dealType ?? null;

  if (dealType === "partnership" || !dealType) {
    // Include selected deal partners on this vehicle
    for (const partner of input.dealPartners ?? []) {
      if (partner.partyId == null || String(partner.partyId).trim() === "") continue;
      const partyId = String(partner.partyId);
      if (seenPartyIds.has(partyId)) continue;

      seenPartyIds.add(partyId);
      const party = partyById.get(partyId);
      const name = party?.name?.trim() || partner.partyName?.trim() || `Partner #${partyId}`;
      options.push({
        value: partyId,
        label: name,
        kind: "party",
        id: partyId,
      });
    }
  }

  if (dealType === "owner_vehicle") {
    // Include owner of vehicle
    const ownerPartner = (input.dealPartners ?? [])[0];
    if (ownerPartner?.partyId != null && String(ownerPartner.partyId).trim() !== "") {
      const partyId = String(ownerPartner.partyId);
      if (!seenPartyIds.has(partyId)) {
        seenPartyIds.add(partyId);
        const party = partyById.get(partyId);
        const name = party?.name?.trim() || ownerPartner.partyName?.trim() || `Owner #${partyId}`;
        options.push({
          value: partyId,
          label: name,
          kind: "party",
          id: partyId,
        });
      }
    }
  }

  // Fallback: If no company/deal options matched yet (or general list fallback)
  if (options.length === 0) {
    for (const party of input.parties ?? []) {
      const partyId = String(party.id);
      if (!partyId || seenPartyIds.has(partyId)) continue;
      seenPartyIds.add(partyId);
      options.push({
        value: partyId,
        label: party.name?.trim() || `Party #${partyId}`,
        kind: "party",
        id: partyId,
      });
    }
  }

  return options;
}

export function defaultPaidByPartyId(partyId?: string | null): string | null {
  return partyId != null && String(partyId).trim() !== "" ? String(partyId).trim() : null;
}

/** Keep Paid By in sync when deal partners / showroom company party change. */
export function syncCostLinePaidBy(
  lines: PurchaseCostLine[] | undefined,
  options: PaidByOption[],
  defaultPartyId: string | null,
): PurchaseCostLine[] {
  const allowed = new Set(options.map((o) => o.value));
  const defaultValue = defaultPartyId ? String(defaultPartyId) : null;

  return (lines ?? []).map((line) => {
    const current = paidBySelectValue(line);
    if (current !== "__none__" && allowed.has(current)) {
      return line;
    }
    if (!defaultValue) {
      return { ...line, paidByPartyId: null };
    }
    return {
      ...line,
      paidByPartyId: defaultValue,
    };
  });
}

export function sumPurchaseCostLines(lines: PurchaseCostLine[] | undefined): number {
  let s = 0;
  for (const l of lines ?? []) {
    const a = Number(l.amount);
    if (Number.isFinite(a) && a > 0) s += a;
  }
  return s;
}

/** Landed cost = base purchase + sum of additional cost lines (all non‑negative). */
export function computeLandedCost(
  basePurchase: number | null | undefined,
  lines: PurchaseCostLine[] | undefined,
): number | null {
  const b =
    basePurchase === null || basePurchase === undefined
      ? NaN
      : Number(basePurchase);
  const baseNum = Number.isFinite(b) && b >= 0 ? b : 0;
  const hasBase = Number.isFinite(b) && b >= 0;
  const sumLines = sumPurchaseCostLines(lines);
  if (!hasBase && sumLines === 0) return null;
  return baseNum + sumLines;
}

type LineAgg = {
  amount: number;
  currencyCode: string | null;
  currencyAmount: number | null;
  exchangeRate: number | null;
  descriptions: string[];
  typeLabel: string | null;
  paidByPartyId: ID | null;
  paidToPartyId: ID | null;
  costBearerPartyId: ID | string | null;
  paymentMethod: PurchaseCostLine["paymentMethod"];
  bankName: string | null;
  transactionReferenceNo: string | null;
  payOrderNo: string | null;
};

/** Merge duplicate typeIds; drop invalid amounts. Do not drop rows just because the local cost-type cache is empty or stale — the API already validated `type_id`. */
export function normalizePurchaseCostLines(lines: PurchaseCostLine[] | undefined): PurchaseCostLine[] {
  const byType = new Map<ID, LineAgg>();
  for (const row of lines ?? []) {
    const typeId = String(row.typeId ?? "").trim();
    if (!typeId) continue;
    const a = Number(row.amount);
    if (!Number.isFinite(a) || a <= 0) continue;
    const note = row.description?.trim() ?? "";
    const label = row.typeLabel?.trim() ? row.typeLabel.trim() : null;
    const currencyCode = row.currencyCode?.trim() || null;
    const currencyAmount = row.currencyAmount != null && Number.isFinite(Number(row.currencyAmount)) ? Number(row.currencyAmount) : null;
    const exchangeRate = row.exchangeRate != null && Number.isFinite(Number(row.exchangeRate)) ? Number(row.exchangeRate) : null;
    const paidByPartyId =
      row.paidByPartyId != null && String(row.paidByPartyId).trim() !== ""
        ? String(row.paidByPartyId)
        : null;
    const paidToPartyId =
      row.paidToPartyId != null && String(row.paidToPartyId).trim() !== ""
        ? String(row.paidToPartyId)
        : null;
    const costBearerPartyId =
      row.costBearerPartyId != null && String(row.costBearerPartyId).trim() !== ""
        ? String(row.costBearerPartyId)
        : null;
    const paymentMethod =
      row.paymentMethod === "cash" ||
      row.paymentMethod === "online" ||
      row.paymentMethod === "pay_order"
        ? row.paymentMethod
        : null;
    const bankName =
      paymentMethod === "online" || paymentMethod === "pay_order"
        ? row.bankName?.trim() || null
        : null;
    const transactionReferenceNo =
      paymentMethod === "online" ? row.transactionReferenceNo?.trim() || null : null;
    const payOrderNo = paymentMethod === "pay_order" ? row.payOrderNo?.trim() || null : null;
    const prev = byType.get(typeId);
    if (prev) {
      prev.amount += a;
      if (note) prev.descriptions.push(note);
      if (!prev.typeLabel && label) prev.typeLabel = label;
      if (!prev.paidByPartyId && paidByPartyId) prev.paidByPartyId = paidByPartyId;
      if (!prev.paidToPartyId && paidToPartyId) prev.paidToPartyId = paidToPartyId;
      if (!prev.costBearerPartyId && costBearerPartyId) prev.costBearerPartyId = costBearerPartyId;
      if (!prev.paymentMethod && paymentMethod) {
        prev.paymentMethod = paymentMethod;
        prev.bankName = bankName;
        prev.transactionReferenceNo = transactionReferenceNo;
        prev.payOrderNo = payOrderNo;
      }
    } else {
      byType.set(typeId, {
        amount: a,
        currencyCode,
        currencyAmount,
        exchangeRate,
        descriptions: note ? [note] : [],
        typeLabel: label,
        paidByPartyId,
        paidToPartyId,
        costBearerPartyId,
        paymentMethod,
        bankName,
        transactionReferenceNo,
        payOrderNo,
      });
    }
  }
  return [...byType.entries()].map(
    ([
      typeId,
      {
        amount,
        currencyCode,
        currencyAmount,
        exchangeRate,
        descriptions,
        typeLabel,
        paidByPartyId,
        paidToPartyId,
        costBearerPartyId,
        paymentMethod,
        bankName,
        transactionReferenceNo,
        payOrderNo,
      },
    ]) => ({
      typeId,
      typeLabel: typeLabel ?? null,
      amount,
      currencyCode,
      currencyAmount,
      exchangeRate,
      paidByPartyId,
      paidToPartyId,
      costBearerPartyId,
      paymentMethod,
      bankName,
      transactionReferenceNo,
      payOrderNo,
      description: descriptions.length ? descriptions.join("; ") : null,
    }),
  );
}

/** Normalize lines and recompute landed cost. Recomputes base purchase price from purchased currency amount & exchange rate if present. */
export function attachNormalizedPurchaseFields(draft: ProductDraft): ProductDraft {
  const lines = normalizePurchaseCostLines(draft.purchaseCostLines);

  let baseNum: number | null = null;
  const pAmt = draft.purchaseCurrencyAmount;
  const exRate = draft.purchaseExchangeRate;

  if (pAmt != null && Number.isFinite(Number(pAmt)) && Number(pAmt) >= 0) {
    const rate = exRate != null && Number.isFinite(Number(exRate)) && Number(exRate) > 0 ? Number(exRate) : 1;
    baseNum = Number(pAmt) * rate;
  } else if (draft.purchasePrice != null && Number.isFinite(Number(draft.purchasePrice)) && Number(draft.purchasePrice) >= 0) {
    baseNum = Number(draft.purchasePrice);
  }

  const landed = computeLandedCost(baseNum, lines);
  const effectiveSalePrice =
    draft.salePrice != null && Number.isFinite(Number(draft.salePrice)) && Number(draft.salePrice) > 0
      ? Number(draft.salePrice)
      : landed != null && Number.isFinite(landed) && landed > 0
      ? Math.round(landed * 1.1)
      : null;

  return {
    ...draft,
    purchasePrice: baseNum,
    purchaseCostLines: lines,
    landedCost: landed,
    salePrice: effectiveSalePrice,
    expectedSalePrice: effectiveSalePrice,
  };
}
